import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { OrganizationDashboard } from './components';
import { ErrorBoundary } from './components/ErrorBoundary';
import { EventsPage, OrganizationPage, ProfileChoicePage } from './pages';
import CreateOrganizationPage from './pages/CreateOrganizationPage';
import EditOrganizationPage from './pages/EditOrganizationPage';
import UserProfilePage from './pages/UserProfilePage';
import InterestedEventsPage from './pages/InterestedEventsPage';
import SavedEventsPage from './pages/SavedEventsPage';
import StaffProfilePage from './pages/StaffProfilePage';
import StaffRequestsPage from './pages/StaffRequestsPage';
import DevLoginPage from './pages/DevLoginPage';
import AdminDashboard from './pages/AdminDashboard';
import LandingPage from './pages/LandingPage';
import SwipeEventsPage from './pages/SwipeEventsPage';
import { EventDetails } from './components/EventDetails';
import { MainLayout } from './components/MainLayout';
import { ROUTES } from './constants/routes';
import { SidebarProvider } from './contexts/SidebarContext';
import { useEffect } from 'react';
import CreateEventPage from './pages/CreateEventPage';
import PersonalInfoPage from './pages/PersonalInfoPage';
import CalendarPage from './pages/CalendarPage';

// Import des composants du nouveau système d'authentification
import AuthModule from './auth/AuthModule';
import { LoginPage, RegisterPage } from './auth';
import ProtectedRoute from './auth/components/ProtectedRoute';
import { AUTH_ROUTES } from './auth/config/routes';

// Nettoyer immédiatement les anciennes données d'auth au démarrage de l'application
export function App() {
  // Nettoyer les anciens tokens JWT au démarrage de l'application
  useEffect(() => {
    // Utiliser une variable pour éviter les logs redondants
    if (!window.__appInitialized) {
      // Vérifier si une redirection Google est en cours
      const isRedirectContext = sessionStorage.getItem('auth_redirect_triggered') === 'true';
      const isPendingRedirect = Object.keys(sessionStorage).some(key => 
        key.includes('pendingRedirect') || key.includes('firebase:authUser')
      );
      
      if (isRedirectContext || isPendingRedirect) {
        // Ne pas nettoyer les tokens si une redirection Google est en cours
        console.log('🛑 Application initialisée - PAS de nettoyage des tokens en raison de redirection SSO');
        console.log('🔍 Contexte de redirection détecté:', { 
          isRedirectContext, 
          isPendingRedirect, 
          sessionStorageKeys: Object.keys(sessionStorage) 
        });
      } else {
        // Aucune redirection en cours, on peut nettoyer
        // Si nous avions une fonction de nettoyage, nous l'appellerions ici
        console.log('Application initialisée - tokens nettoyés');
      }
      
      window.__appInitialized = true;
    }
  }, []);

  return (
    <Router>
      <AuthModule>
        <SidebarProvider>
          <ErrorBoundary>
            <Routes>
              {/* La page d'accueil est désormais la première route */}
              <Route path={ROUTES.HOME} element={<LandingPage />} />
              <Route path={ROUTES.LANDING} element={<LandingPage />} />
              
              {/* Routes publiques sans authentification requise */}
              <Route path={ROUTES.SIGNUP} element={<RegisterPage />} />
              <Route path="/register" element={<RegisterPage />} /> {/* Route supplémentaire pour /register */}
              <Route path={ROUTES.LOGIN} element={<LoginPage />} />
              <Route path="/dev-login" element={<DevLoginPage />} />
              
              {/* Layout partagé pour toutes les routes protégées */}
              <Route 
                element={
                  <ProtectedRoute redirectPath={AUTH_ROUTES.LOGIN}>
                    <MainLayout />
                  </ProtectedRoute>
                }
              >
                {/* Pages accessibles pour tous les utilisateurs connectés */}
                <Route path="/events" element={<EventsPage />} />
                <Route path="/dashboard" element={<EventsPage />} />
                <Route path={ROUTES.SWIPE_EVENTS} element={<SwipeEventsPage />} />
                <Route path={ROUTES.USER_PROFILE} element={<UserProfilePage />} />
                <Route path={ROUTES.PERSONAL_INFO} element={<PersonalInfoPage />} />
                <Route path={ROUTES.SAVED_EVENTS} element={<SavedEventsPage />} />
                <Route path={ROUTES.INTERESTED_EVENTS} element={<InterestedEventsPage />} />
                <Route path={ROUTES.CALENDAR} element={<CalendarPage />} />
                <Route path="/events/:id" element={<EventDetails />} />
                
                {/* Pages staff accessibles à tous */}
                <Route path={ROUTES.STAFF_PROFILE} element={<StaffProfilePage />} />
                <Route path="/staff/requests/event/:id" element={<StaffRequestsPage />} />
                
                {/* Pages accessibles uniquement aux org_admin et admin */}
                <Route path={ROUTES.ORGANIZATION_CREATE} element={
                  <ProtectedRoute requiredRoles={['org_admin', 'admin']}>
                    <CreateOrganizationPage />
                  </ProtectedRoute>
                } />
                <Route path="/organization/new" element={
                  <ProtectedRoute requiredRoles={['org_admin', 'admin']}>
                    <CreateOrganizationPage />
                  </ProtectedRoute>
                } />
                <Route path={ROUTES.ORGANIZATION_EDIT.replace(':id', ':id')} element={
                  <ProtectedRoute requiredRoles={['org_admin', 'admin']}>
                    <EditOrganizationPage />
                  </ProtectedRoute>
                } />
                <Route path="/organization/edit/:id" element={
                  <ProtectedRoute requiredRoles={['org_admin', 'admin']}>
                    <EditOrganizationPage />
                  </ProtectedRoute>
                } />
                <Route path={ROUTES.ORGANIZATION} element={
                  <ProtectedRoute requiredRoles={['org_admin', 'admin']}>
                    <OrganizationDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/organization" element={
                  <ProtectedRoute requiredRoles={['org_admin', 'admin']}>
                    <OrganizationDashboard />
                  </ProtectedRoute>
                } />
                <Route path={`${ROUTES.ORGANIZATION}/:id`} element={
                  <ProtectedRoute requiredRoles={['org_admin', 'admin']}>
                    <OrganizationPage />
                  </ProtectedRoute>
                } />
                <Route path="/organization/:id" element={
                  <ProtectedRoute requiredRoles={['org_admin', 'admin']}>
                    <OrganizationPage />
                  </ProtectedRoute>
                } />
                <Route path={ROUTES.PROFILE_CHOICE} element={
                  <ProtectedRoute requiredRoles={['org_admin', 'admin']}>
                    <ProfileChoicePage />
                  </ProtectedRoute>
                } />
                <Route path="/events/new" element={
                  <ProtectedRoute requiredRoles={['org_admin', 'admin']}>
                    <CreateEventPage />
                  </ProtectedRoute>
                } />
                
                {/* Route accessible uniquement aux admins */}
                <Route path={ROUTES.ADMIN} element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <AdminDashboard />
                  </ProtectedRoute>
                } />
              </Route>
            </Routes>
          </ErrorBoundary>
        </SidebarProvider>
      </AuthModule>
    </Router>
  );
}
